import React, { FC, useMemo } from 'react';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { deepEqual } from 'fast-equals';
import { Add, Save, Close, Work, Build } from '@mui/icons-material';
// Components
import { Modal, Loader, Table, Card } from '../../../components';
import {
  Card as CardMui,
  Fade,
  ButtonGroup,
  Button,
  CardContent,
  CardHeader,
  TextField,
  Grid,
  Paper,
  Typography,
  useMediaQuery
} from '@mui/material';
// models
import { IFramerDetail } from '../../../models';
// fetch
import { postFramer, updateFramer } from '../../../fetch';
import { formatDate, formatInputPhoneNumber, phoneRegExp } from '../../../helpers';
import { MobileTable } from '../../../components/table/MobileTable';
import { useSnackbar } from 'notistack';

interface IFramerModal {
  open: boolean;
  modalIsLoading: boolean;
  onClose: () => void;
  currentFramer: IFramerDetail | null;
  fetchFramers: () => void;
}

const FramerSchema = Yup.object().shape({
  businessName: Yup.string().max(200, 'Max 200 characters').required('Required'),
  buildLimit: Yup.string().max(200, 'Max 200 characters').required('Required'),
  contactName: Yup.string().max(200, 'Max 200 characters'),
  contactEmail: Yup.string()
    .max(200, 'Max 200 characters')
    .required('Required')
    .email('Invalid email address'),
  contactPhone: Yup.string().matches(phoneRegExp, {
    excludeEmptyString: true,
    message: 'Invalid phone number'
  })
});

export const FramerModal: FC<IFramerModal> = ({
  open,
  modalIsLoading,
  onClose,
  currentFramer,
  fetchFramers
}) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const isMobile = useMediaQuery('(max-width: 960px)');

  const activeBuildsColumns = useMemo(() => {
    return [
      {
        Header: 'Neighborhood',
        accessor: 'neighborhoodDescription',
        sort: false
      },
      {
        Header: 'Lot',
        accessor: 'lotNumber',
        sort: false
      },
      {
        Header: 'Status',
        accessor: 'jobStatus',
        sort: false
      },
      {
        Header: 'Frame Date',
        accessor: 'frameDate',
        sort: false,
        Cell: props => <>{formatDate(props.cell.row.original.frameDate)}</>
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={{
          businessName:
            currentFramer && currentFramer.businessName ? currentFramer.businessName : '',
          buildLimit: currentFramer && currentFramer.buildLimit ? currentFramer.buildLimit : '',
          contactName: currentFramer && currentFramer.contactName ? currentFramer.contactName : '',
          contactEmail:
            currentFramer && currentFramer.contactEmail ? currentFramer.contactEmail : '',
          contactPhone:
            currentFramer && currentFramer.contactPhone ? currentFramer.contactPhone : ''
        }}
        validationSchema={FramerSchema}
        onSubmit={async (values, actions) => {
          if (currentFramer) {
            try {
              await updateFramer(
                currentFramer.framerId,
                values.businessName,
                Number(values.buildLimit),
                values.contactName,
                values.contactEmail,
                values.contactPhone
              );

              enqueueSnackbar('Framer updated!', {
                variant: 'success'
              });
              onClose();
              fetchFramers();
              actions.resetForm();
            } catch (error) {
              enqueueSnackbar(error?.Detail ?? 'Error updating Framer.', {
                variant: 'error'
              });
            }
          } else {
            try {
              await postFramer(
                values.businessName,
                Number(values.buildLimit),
                values.contactName,
                values.contactEmail,
                values.contactPhone
              );

              enqueueSnackbar('Framer added!', {
                variant: 'success'
              });
              onClose();
              fetchFramers();
              actions.resetForm();
            } catch (error) {
              enqueueSnackbar(error?.Detail ?? 'Error creating Framer.', {
                variant: 'error'
              });
            }
          }
        }}
      >
        {({
          resetForm,
          isSubmitting,
          values,
          initialValues,
          setFieldValue,
          errors,
          touched,
          handleSubmit,
          dirty,
          isValid,
          handleBlur
        }) => {
          return (
            <Modal
              open={open}
              onClose={() => {
                if (!deepEqual(initialValues, values)) {
                  const result = window.confirm(
                    'You have unsaved changes, are you sure you want to exit?'
                  );
                  if (result) {
                    resetForm();
                    onClose();
                  } else {
                    return;
                  }
                } else {
                  onClose();
                  resetForm();
                }
              }}
              maxWidth="md"
              title={currentFramer ? 'EDIT FRAMER' : 'ADD NEW FRAMER'}
            >
              {modalIsLoading && <Loader type="overlay" position="centered" />}
              {isSubmitting && <Loader type="overlay" position="centered" />}
              <Fade in={open}>
                <Form onSubmit={handleSubmit} autoComplete="none">
                  <div className={classes.content}>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <CardMui elevation={0} variant="outlined" className={classes.marginBottom}>
                          <CardHeader
                            avatar={<Work />}
                            title="INFO"
                            className={classes.primaryHeader}
                          />
                          <CardContent className={classes.cardContent}>
                            <Paper elevation={0} square>
                              <div className={classes.column}>
                                <Grid container spacing={1}>
                                  <Grid item xs={12} sm={8}>
                                    <TextField
                                      fullWidth
                                      required={currentFramer ? false : true}
                                      disabled={currentFramer ? true : false}
                                      variant="outlined"
                                      autoComplete="nope"
                                      label="Business Name"
                                      name="businessName"
                                      value={values.businessName}
                                      onBlur={handleBlur}
                                      size="small"
                                      onChange={e => setFieldValue('businessName', e.target.value)}
                                      error={Boolean(touched.businessName && errors.businessName)}
                                      helperText={touched.businessName && errors.businessName}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={4}>
                                    <TextField
                                      fullWidth
                                      required
                                      variant="outlined"
                                      autoComplete="nope"
                                      label="Build Limit"
                                      name="buildLimit"
                                      size="small"
                                      value={values.buildLimit}
                                      onBlur={handleBlur}
                                      onChange={e => {
                                        const re = /^[0-9\b]+$/;
                                        // if value is not blank, then test the regex
                                        if (e.target.value === '' || re.test(e.target.value)) {
                                          setFieldValue('buildLimit', e.target.value);
                                        }
                                      }}
                                      inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                      error={Boolean(touched.buildLimit && errors.buildLimit)}
                                      helperText={touched.buildLimit && errors.buildLimit}
                                    />
                                  </Grid>
                                  <Grid item xs={12}>
                                    <TextField
                                      fullWidth
                                      variant="outlined"
                                      autoComplete="nope"
                                      label="Contact Name"
                                      name="contactName"
                                      size="small"
                                      value={values.contactName}
                                      onBlur={handleBlur}
                                      onChange={e => setFieldValue('contactName', e.target.value)}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={6}>
                                    <TextField
                                      fullWidth
                                      required
                                      variant="outlined"
                                      autoComplete="nope"
                                      label="Email"
                                      name="contactEmail"
                                      size="small"
                                      value={values.contactEmail}
                                      onBlur={handleBlur}
                                      onChange={e => setFieldValue('contactEmail', e.target.value)}
                                      error={Boolean(touched.contactEmail && errors.contactEmail)}
                                      helperText={touched.contactEmail && errors.contactEmail}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={6}>
                                    <TextField
                                      fullWidth
                                      variant="outlined"
                                      autoComplete="nope"
                                      label="Phone Number"
                                      name="contactPhone"
                                      size="small"
                                      value={values.contactPhone}
                                      onBlur={handleBlur}
                                      onChange={e =>
                                        setFieldValue(
                                          'contactPhone',
                                          formatInputPhoneNumber(e.target.value)
                                        )
                                      }
                                      error={Boolean(touched.contactPhone && errors.contactPhone)}
                                      helperText={touched.contactPhone && errors.contactPhone}
                                    />
                                  </Grid>
                                </Grid>
                              </div>
                            </Paper>
                          </CardContent>
                        </CardMui>
                        {currentFramer && (
                          <Card title="Active Builds" icon={<Build />}>
                            {currentFramer?.activeBuilds?.length === 0 && (
                              <Typography align="center">No active builds.</Typography>
                            )}
                            {currentFramer?.activeBuilds?.length > 0 && (
                              <Table
                                columns={activeBuildsColumns}
                                data={currentFramer?.activeBuilds ?? []}
                                hidePagination
                                mobileProps={{
                                  fields: activeBuildsColumns
                                    .filter(c => c.Header)
                                    .map(c => ({
                                      name: c.Header,
                                      accessor: c.accessor,
                                      Cell: c?.Cell
                                    }))
                                }}
                                ResponsiveComponent={isMobile ? MobileTable : undefined}
                              />
                            )}
                          </Card>
                        )}
                      </Grid>
                    </Grid>
                  </div>
                  <ButtonGroup>
                    <Button
                      disabled={!dirty || isSubmitting || !isValid}
                      type="submit"
                      startIcon={currentFramer ? <Save /> : <Add />}
                      variant="contained"
                      color="primary"
                    >
                      {currentFramer ? 'Save' : 'Add Framer'}
                    </Button>
                    <Button
                      type="button"
                      variant="contained"
                      color="inherit"
                      startIcon={<Close />}
                      onClick={() => {
                        if (!deepEqual(initialValues, values)) {
                          const result = window.confirm(
                            'You have unsaved changes, are you sure you want to exit?'
                          );
                          if (result) {
                            resetForm();
                            onClose();
                          } else {
                            return;
                          }
                        } else {
                          onClose();
                        }
                      }}
                    >
                      Cancel
                    </Button>
                  </ButtonGroup>
                </Form>
              </Fade>
            </Modal>
          );
        }}
      </Formik>
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  primaryHeader: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    marginBottom: theme.spacing(1)
  },
  marginBottom: {
    marginBottom: theme.spacing(1)
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    '& > div:not(:first-of-type)': {
      marginTop: theme.spacing(1)
    }
  },
  outlinedLabel: {
    backgroundColor: theme.palette.common.white,
    paddingLeft: 2,
    paddingRight: 2
  },
  content: {
    marginTop: theme.spacing(1)
  },
  cardContent: {
    paddingTop: 0
  },
  paperBorder: {
    border: `1px solid ${theme.palette.grey[300]}`
  }
}));
